// WbTicketsPage — /weighbridge/tickets (Phase C9).
//
// Server-side paginated list of all tickets with multi-select status,
// direction, date range, yard, and customer filters. Row click opens
// the ticket detail page. "+ New Ticket" routes to /tickets/new.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Lock, Plus, Receipt } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { extractErrorMessage } from '../../../utils/errorMessage';
import { useToastStore } from '../../../store/toastStore';
import { WbCustomerSearch } from '../components/WbCustomerSearch';
import { useWbYards } from '../hooks/useWbYards';
import {
  useWbTickets,
  type TicketDirection,
  type TicketStatus,
  type TicketSummaryDto,
} from '../hooks/useWbTickets';
import { NotesBadge } from '../../../components/notes/NotesBadge';
import { NotesPanel } from '../../../components/notes/NotesPanel';
import styles from './WbTicketsPage.module.css';

const STATUS_OPTIONS: ReadonlyArray<{
  value: TicketStatus;
  label: string;
}> = [
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'completed', label: 'Completed' },
  { value: 'voided', label: 'Voided' },
];

// -- Formatters / helpers ---------------------------------------------------

const GBP = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
});

function fmtCurrency(n: number): string {
  return GBP.format(n);
}

function fmtKg(n: number | null): string {
  if (n === null) return '—';
  return `${n.toLocaleString('en-GB')} kg`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB');
}

function defaultDateFromIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// Terminal statuses that cannot be deleted — mirrors the server guard in
// wb/tickets/service.ts (NON_DELETABLE_TICKET_STATUSES). Completed / voided
// tickets carry financial / audit weight; everything else is deletable.
const NON_DELETABLE_STATUSES: ReadonlyArray<TicketStatus> = [
  'completed',
  'voided',
];

export function statusBadge(status: TicketStatus): JSX.Element {
  if (status === 'draft') return <Badge variant="muted">Draft</Badge>;
  if (status === 'open') return <Badge variant="info">Open</Badge>;
  if (status === 'completed') return <Badge variant="success">Completed</Badge>;
  return <Badge variant="danger">Voided</Badge>;
}

function directionBadge(d: TicketDirection): JSX.Element {
  return <Badge>{titleCase(d)}</Badge>;
}

// -- Yard filter (mirrors WbStockPage) --------------------------------------

function YardFilter({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}): JSX.Element {
  const q = useWbYards();
  return (
    <label className={styles.filterField}>
      <span>Yard</span>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All yards</option>
        {(q.data?.yards ?? []).map((y) => (
          <option key={y.enc_id} value={y.enc_id}>
            {y.name}
          </option>
        ))}
      </select>
    </label>
  );
}

// -- Page -------------------------------------------------------------------

export function WbTicketsPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pushToast = useToastStore((s) => s.push);
  const [deletingEncId, setDeletingEncId] = useState<string | null>(null);
  const [notesTarget, setNotesTarget] = useState<TicketSummaryDto | null>(null);

  const [statusSet, setStatusSet] = useState<Set<TicketStatus>>(
    new Set<TicketStatus>(),
  );
  const [direction, setDirection] = useState<'' | TicketDirection>('');
  const [dateFrom, setDateFrom] = useState<string>(defaultDateFromIso());
  const [dateTo, setDateTo] = useState<string>(todayIso());
  const [yardId, setYardId] = useState<string>('');
  const [customerEncId, setCustomerEncId] = useState<string>('');
  const [page, setPage] = useState<number>(1);

  const statusList = useMemo<TicketStatus[]>(
    () => [...statusSet],
    [statusSet],
  );

  const ticketsQuery = useWbTickets(
    {
      status: statusList.length > 0 ? statusList : undefined,
      direction: direction || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      yard_enc_id: yardId || undefined,
      customer_enc_id: customerEncId.trim() || undefined,
      page,
      per_page: 25,
    },
    // Poll so the active-lock icon (migration 167) refreshes without a manual
    // reload — mirrors the kiosk list, which already enables polling.
    { enablePolling: true },
  );
  const rows = ticketsQuery.data?.tickets ?? [];
  const pagination = ticketsQuery.data?.pagination;

  function toggleStatus(s: TicketStatus) {
    setStatusSet((prev) => {
      const next = new Set(prev);
      if (next.has(s)) {
        next.delete(s);
      } else {
        next.add(s);
      }
      return next;
    });
    setPage(1);
  }

  function withReset<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  // Soft-delete a ticket from the list. The server allows any non-terminal
  // ticket (completed / voided are immutable and go through the void path),
  // so the Delete action is hidden for those. On success we invalidate the
  // broad ['wb', 'tickets'] prefix so every paginated/filtered list refetches.
  async function handleDeleteTicket(encId: string): Promise<void> {
    if (!window.confirm('Delete this ticket? This cannot be undone.')) {
      return;
    }
    setDeletingEncId(encId);
    try {
      await apiClient.delete(endpoints.wb.ticket(encId));
      await queryClient.invalidateQueries({ queryKey: ['wb', 'tickets'] });
    } catch (err) {
      pushToast({ type: 'error', message: extractErrorMessage(err) });
    } finally {
      setDeletingEncId(null);
    }
  }

  function resetFilters() {
    setStatusSet(new Set<TicketStatus>());
    setDirection('');
    setCustomerEncId('');
    setDateFrom(defaultDateFromIso());
    setDateTo(todayIso());
    setYardId('');
    setPage(1);
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Tickets"
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/weighbridge/tickets/new')}
          >
            New Ticket
          </Button>
        }
      />

      <div className={styles.filterBar} data-testid="tickets-filters">
        <fieldset className={styles.statusFilter}>
          <legend>Status</legend>
          {STATUS_OPTIONS.map((s) => (
            <label key={s.value} className={styles.statusChip}>
              <input
                type="checkbox"
                checked={statusSet.has(s.value)}
                onChange={() => toggleStatus(s.value)}
              />
              {s.label}
            </label>
          ))}
        </fieldset>
        <label className={styles.filterField}>
          <span>Direction</span>
          <select
            value={direction}
            onChange={(e) =>
              withReset(setDirection)(e.target.value as '' | TicketDirection)
            }
          >
            <option value="">All</option>
            <option value="inwards">Inwards</option>
            <option value="outwards">Outwards</option>
          </select>
        </label>
        <label className={styles.filterField}>
          <span>Date from</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => withReset(setDateFrom)(e.target.value)}
          />
        </label>
        <label className={styles.filterField}>
          <span>Date to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => withReset(setDateTo)(e.target.value)}
          />
        </label>
        <YardFilter value={yardId} onChange={withReset(setYardId)} />
        <label className={styles.filterField}>
          <span>Customer</span>
          <WbCustomerSearch
            value={customerEncId === '' ? null : customerEncId}
            onChange={(encId) => withReset(setCustomerEncId)(encId ?? '')}
            placeholder="All customers"
          />
        </label>
        <div className={styles.filterActions}>
          <Button variant="secondary" size="sm" onClick={resetFilters}>
            Reset
          </Button>
        </div>
      </div>

      {ticketsQuery.isError ? (
        <p className={styles.error}>
          {extractErrorMessage(ticketsQuery.error)}
        </p>
      ) : null}

      <Card flush>
        {ticketsQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading tickets" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Receipt}
            title="No tickets"
            description="Try widening your date range or removing filters."
          />
        ) : (
          <table className={styles.table} data-testid="tickets-table">
            <thead>
              <tr>
                <th>Ticket #</th>
                <th>Customer</th>
                <th>VRM</th>
                <th>Direction</th>
                <th>Yard</th>
                <th>Status</th>
                <th className={styles.lockCell} aria-label="Lock" />
                <th className={styles.numCol}>Net weight</th>
                <th className={styles.numCol}>Total</th>
                <th>Date</th>
                <th className={styles.actionsCell}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t: TicketSummaryDto) => (
                <tr
                  key={t.enc_id}
                  className={styles.rowClickable}
                  onClick={() => navigate(`/weighbridge/tickets/${t.enc_id}`)}
                >
                  <td>
                    {t.ticket_number === null ? (
                      <span className={styles.draftMarker}>Draft</span>
                    ) : (
                      t.ticket_number
                    )}
                  </td>
                  <td>{t.customer.name}</td>
                  <td>{t.vehicle_reg_snapshot ?? '—'}</td>
                  <td>{directionBadge(t.direction)}</td>
                  <td>{t.yard.name}</td>
                  <td>{statusBadge(t.status)}</td>
                  <td className={styles.lockCell}>
                    {t.lock &&
                    new Date(t.lock.expires_at).getTime() > Date.now() ? (
                      <span
                        className={styles.lockIcon}
                        title={`Locked by ${t.lock.locked_by_name} · ${t.lock.origin} · expires ${new Date(
                          t.lock.expires_at,
                        ).toLocaleTimeString()}`}
                        data-testid={`lock-icon-${t.enc_id}`}
                      >
                        <Lock size={14} />
                      </span>
                    ) : null}
                    <NotesBadge
                      count={t.notes_count}
                      onClick={(e) => { e.stopPropagation(); setNotesTarget(t); }}
                    />
                  </td>
                  <td className={styles.numCol}>{fmtKg(t.net_weight_kg)}</td>
                  <td className={styles.numCol}>{fmtCurrency(t.total)}</td>
                  <td>
                    {fmtDate(t.completed_at ?? t.created_at)}
                  </td>
                  <td
                    className={styles.actionsCell}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!NON_DELETABLE_STATUSES.includes(t.status) ? (
                      <Button
                        variant="danger"
                        size="sm"
                        loading={deletingEncId === t.enc_id}
                        disabled={deletingEncId !== null}
                        onClick={() => {
                          void handleDeleteTicket(t.enc_id);
                        }}
                        data-testid={`ticket-delete-${t.enc_id}`}
                      >
                        Delete
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {pagination && pagination.total > 0 ? (
        <div className={styles.pagination}>
          <span>
            Page {pagination.page} of {pagination.total_pages} ·{' '}
            {pagination.total} ticket{pagination.total === 1 ? '' : 's'}
          </span>
          <div className={styles.paginationButtons}>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() =>
                setPage((p) => Math.min(pagination.total_pages, p + 1))
              }
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      {notesTarget && (
        <NotesPanel
          open={true}
          onClose={() => setNotesTarget(null)}
          entityType="wb_ticket"
          encId={notesTarget.enc_id}
          entityLabel={`Ticket ${notesTarget.ticket_number ?? notesTarget.enc_id}`}
        />
      )}
    </div>
  );
}
